import { z } from 'zod';
import { qualityGateExitCode, type QualityGateVerdict } from './quality-gates';

/**
 * Requirement #18 — Quality Analytics & Release Confidence.
 * Compose-on-read over #10–#17 evidence. No second scorer table / vanity %.
 * Advisory only — never executes tests or mutates gates.
 */

export const RELEASE_CONFIDENCE_VERDICTS = ['go', 'conditional', 'no_go', 'insufficient_evidence'] as const;
export type ReleaseConfidenceVerdict = (typeof RELEASE_CONFIDENCE_VERDICTS)[number];

/** Product labels aligned with Quality HQ Ship / Investigate / Hold. */
export const RELEASE_CONFIDENCE_ACTIONS = ['ship', 'investigate', 'hold', 'wait'] as const;
export type ReleaseConfidenceAction = (typeof RELEASE_CONFIDENCE_ACTIONS)[number];

export const RELEASE_CONFIDENCE_LEVELS = ['high', 'medium', 'low', 'unknown'] as const;
export type ReleaseConfidenceLevel = (typeof RELEASE_CONFIDENCE_LEVELS)[number];

export const RELEASE_CONFIDENCE_SOURCES = [
  'execution',
  'quality_gate',
  'risk',
  'coverage',
  'maintenance',
  'change_impact',
  'production',
  'release_tiles',
  'requirement',
  'scenario',
  /** Wave 5C — optional contract-check drift (compose from Change Impact 1D). */
  'contract',
  /** Wave 5C — optional flake hypotheses (compose from Change Impact 1A). */
  'flake',
  /** #12 → #22 — environment evidence bands (compose from environment intelligence). */
  'environment',
] as const;
export type ReleaseConfidenceSource = (typeof RELEASE_CONFIDENCE_SOURCES)[number];

export const ReleaseConfidenceEvidenceSchema = z.object({
  source: z.enum(RELEASE_CONFIDENCE_SOURCES),
  sourceId: z.string().max(80).nullable().optional(),
  status: z.string().max(80),
  severity: z.enum(['info', 'low', 'medium', 'high', 'critical']).default('info'),
  reason: z.string().max(500),
  timestamp: z.string().datetime().optional(),
});
export type ReleaseConfidenceEvidence = z.infer<typeof ReleaseConfidenceEvidenceSchema>;

export const ReleaseConfidenceEvaluateRequestSchema = z.object({
  commitSha: z.string().trim().min(7).max(64).optional(),
  releaseId: z.string().trim().max(120).optional(),
  flowIds: z.array(z.string().uuid()).max(200).optional(),
  scenarioIds: z.array(z.string().uuid()).max(200).optional(),
  requirementIds: z.array(z.string().uuid()).max(100).optional(),
  runIds: z.array(z.string().uuid()).max(50).optional(),
  includeScenarios: z.boolean().optional().default(true),
  includeFlows: z.boolean().optional().default(true),
  /** When true, also load legacy release-readiness tiles for the SHA (cache OK). */
  includeLegacyTiles: z.boolean().optional().default(true),
  /**
   * Wave 5C — compose contract drift + flake evidence from Change Impact.
   * Default true (display). Absence stays unknown — never invents healthy.
   */
  includeContractFlakeEvidence: z.boolean().optional().default(true),
  /**
   * Wave 5C — org opt-in: allow contract/flake concerns to tip verdict to
   * conditional only. Default false = verdict formula unchanged (never no_go).
   */
  weightContractFlakeInVerdict: z.boolean().optional().default(false),
  /**
   * #12 → #22 — compose environment-intelligence bands into Release Confidence.
   * Default true (display). Unknown stays unknown — never invents healthy env.
   */
  includeEnvironmentEvidence: z.boolean().optional().default(true),
  /**
   * #12 → #22 — opt-in: allow conflicting/partial environment bands to tip
   * verdict to conditional only. Default false = verdict formula unchanged.
   * Unknown never tips; never drives no_go / auto-block alone.
   */
  weightEnvironmentInVerdict: z.boolean().optional().default(false),
});
export type ReleaseConfidenceEvaluateRequest = z.input<typeof ReleaseConfidenceEvaluateRequestSchema>;

export const ReleaseDimensionSummarySchema = z.object({
  status: z.enum(['ok', 'warning', 'blocking', 'unknown']),
  headline: z.string().max(300),
  detail: z.string().max(500).optional(),
  evidenceCount: z.number().int().nonnegative().default(0),
});
export type ReleaseDimensionSummary = z.infer<typeof ReleaseDimensionSummarySchema>;

export const ReleaseConfidenceResponseSchema = z.object({
  projectId: z.string().uuid(),
  commitSha: z.string().nullable(),
  releaseId: z.string().nullable().optional(),
  verdict: z.enum(RELEASE_CONFIDENCE_VERDICTS),
  /** Ship / Investigate / Hold / Wait — UI-facing action label. */
  action: z.enum(RELEASE_CONFIDENCE_ACTIONS),
  confidence: z.enum(RELEASE_CONFIDENCE_LEVELS),
  recommendation: z.string().max(500),
  reasons: z.array(z.string().max(500)).max(30),
  evidence: z.array(ReleaseConfidenceEvidenceSchema).max(80),
  missingEvidence: z.array(z.string().max(300)).max(20),
  executionHealth: ReleaseDimensionSummarySchema,
  qualityGates: ReleaseDimensionSummarySchema,
  risk: ReleaseDimensionSummarySchema,
  coverage: ReleaseDimensionSummarySchema,
  maintenance: ReleaseDimensionSummarySchema,
  changeImpact: ReleaseDimensionSummarySchema,
  production: ReleaseDimensionSummarySchema,
  /**
   * Wave 5C — optional contract-check drift dimension (related_contract_check).
   * `unknown` when no checks / unavailable — never means “contracts healthy.”
   */
  contract: ReleaseDimensionSummarySchema,
  /**
   * Wave 5C — optional flake hypothesis dimension (related_historical).
   * `unknown` when no flake window — never means “no flakes forever.”
   */
  flake: ReleaseDimensionSummarySchema,
  /**
   * #12 → #22 — environment evidence dimension (known/partial/unknown/conflicting).
   * `unknown` ≠ healthy environment; never a positive release signal alone.
   */
  environment: ReleaseDimensionSummarySchema,
  /** Echo of #12 overall evidence band when composed. */
  environmentEvidenceBand: z.enum(['known', 'partial', 'unknown', 'conflicting']).nullable().optional(),
  /** Deep link to contract checks when evidence was composed. */
  contractDeepLink: z.string().max(500).nullable().optional(),
  /** Deep link to failures / FI when flake evidence was composed. */
  flakeDeepLink: z.string().max(500).nullable().optional(),
  /** Deep link to project environment intelligence when composed. */
  environmentDeepLink: z.string().max(500).nullable().optional(),
  /** Existing #11/#12 exit-code compatibility — not a second CI system. */
  gateCompatibleExitCode: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  /** Mapped gate verdict for CI consumers of rynora-quality-gate.json shape. */
  gateCompatibleVerdict: z.enum(['pass', 'warn', 'fail']),
  /** Optional legacy green/amber/red from release_verdicts tiles. */
  legacyReleaseVerdict: z.enum(['green', 'amber', 'red']).nullable().optional(),
  /**
   * Wave 5C — true when contract/flake concerns were allowed to tip verdict
   * (request weightContractFlakeInVerdict). Never auto-blocks alone.
   */
  contractFlakeWeightedInVerdict: z.boolean().default(false),
  /**
   * #12 → #22 — true when environment concerns were allowed to tip verdict
   * (request weightEnvironmentInVerdict). Never auto-blocks alone.
   */
  environmentWeightedInVerdict: z.boolean().default(false),
  mode: z.enum(['automation_first', 'quality_first', 'mixed']),
  /** Explicit locks — #18 never executes or rewrites. */
  executed: z.literal(false),
  rewritten: z.literal(false),
  aiAdvisoryOnly: z.literal(true),
  /** Explicit — environment correlation never auto-blocks release. */
  autoBlocksRelease: z.literal(false),
  evaluatedAt: z.string(),
});
export type ReleaseConfidenceResponse = z.infer<typeof ReleaseConfidenceResponseSchema>;

export function scrubReleaseConfidenceText(text: string): string {
  return text
    .replace(/\$\{SECRET\.[^}]+\}/gi, '${SECRET.REDACTED}')
    .replace(/\b(password|passwd|secret|api[_-]?key|token|bearer|authorization)\s*[:=]\s*\S+/gi, '$1=[REDACTED]')
    .replace(/Bearer\s+\S+/gi, 'Bearer [REDACTED]')
    .slice(0, 500);
}

export function actionFromVerdict(verdict: ReleaseConfidenceVerdict): ReleaseConfidenceAction {
  if (verdict === 'go') return 'ship';
  if (verdict === 'conditional') return 'investigate';
  if (verdict === 'no_go') return 'hold';
  return 'wait';
}

/** Map #18 verdict onto existing quality-gate exit codes — one CI system. */
export function gateCompatFromVerdict(verdict: ReleaseConfidenceVerdict): {
  exitCode: 0 | 1 | 2;
  gateVerdict: QualityGateVerdict;
} {
  if (verdict === 'go') return { exitCode: qualityGateExitCode('pass'), gateVerdict: 'pass' };
  if (verdict === 'no_go') return { exitCode: qualityGateExitCode('fail'), gateVerdict: 'fail' };
  return { exitCode: qualityGateExitCode('warn'), gateVerdict: 'warn' };
}

export function confidenceFromEvidenceSources(sourceCount: number, hasBlocking: boolean): ReleaseConfidenceLevel {
  if (sourceCount <= 0) return 'unknown';
  if (sourceCount === 1) return 'low';
  if (sourceCount <= 3) return hasBlocking ? 'medium' : 'medium';
  return hasBlocking ? 'high' : 'high';
}

export type ReleaseConfidenceSignals = {
  hasAnyEvidence: boolean;
  evidenceSourceCount: number;
  gateFailed: boolean;
  gateWarned: boolean;
  criticalProductionOpen: boolean;
  productionOpenNonCritical: boolean;
  riskCritical: boolean;
  riskHigh: boolean;
  riskMedium: boolean;
  maintenanceBlocking: boolean;
  maintenanceOpen: boolean;
  changeImpactHigh: boolean;
  changeNeedsReview: boolean;
  executionUnstable: boolean;
  executionHealingConcern: boolean;
  coverageUnknown: boolean;
  coveragePartial: boolean;
  missingRequiredEvidence: boolean;
  /**
   * Wave 5C — set only when weightContractFlakeInVerdict and contract
   * failures exist. Never drives no_go by itself.
   */
  contractConcern: boolean;
  /**
   * Wave 5C — set only when weightContractFlakeInVerdict and flake
   * hypotheses exist. Never drives no_go by itself.
   */
  flakeConcern: boolean;
  /**
   * #12 → #22 — set only when weightEnvironmentInVerdict and band is
   * conflicting or partial. Unknown never sets this. Never drives no_go.
   */
  environmentConcern: boolean;
};

/**
 * Deterministic verdict — explainable rules only.
 * No vanity overall percentage.
 */
export function deriveReleaseConfidenceVerdict(s: ReleaseConfidenceSignals): ReleaseConfidenceVerdict {
  if (!s.hasAnyEvidence || s.missingRequiredEvidence) {
    return 'insufficient_evidence';
  }
  if (s.gateFailed || s.criticalProductionOpen || s.riskCritical) {
    return 'no_go';
  }
  if (
    s.gateWarned ||
    s.productionOpenNonCritical ||
    s.riskHigh ||
    s.riskMedium ||
    s.maintenanceBlocking ||
    s.maintenanceOpen ||
    s.changeImpactHigh ||
    s.changeNeedsReview ||
    s.executionUnstable ||
    s.executionHealingConcern ||
    s.coveragePartial ||
    s.contractConcern ||
    s.flakeConcern ||
    s.environmentConcern
  ) {
    return 'conditional';
  }
  if (s.coverageUnknown && s.evidenceSourceCount < 2) {
    return 'insufficient_evidence';
  }
  return 'go';
}

export function recommendationForVerdict(verdict: ReleaseConfidenceVerdict, reasons: string[]): string {
  const head =
    verdict === 'go'
      ? 'Ship — available evidence supports release.'
      : verdict === 'conditional'
        ? 'Investigate — release may proceed only after reviewing warnings.'
        : verdict === 'no_go'
          ? 'Hold — blocking evidence prevents a safe release recommendation.'
          : 'Wait — insufficient evidence to recommend go or no-go.';
  const top = reasons.slice(0, 2).map(scrubReleaseConfidenceText);
  return scrubReleaseConfidenceText(top.length ? `${head} ${top.join(' ')}` : head);
}

export function buildReleaseConfidenceResponse(input: {
  projectId: string;
  commitSha: string | null;
  releaseId?: string | null;
  signals: ReleaseConfidenceSignals;
  evidence: ReleaseConfidenceEvidence[];
  missingEvidence: string[];
  reasons: string[];
  executionHealth: ReleaseDimensionSummary;
  qualityGates: ReleaseDimensionSummary;
  risk: ReleaseDimensionSummary;
  coverage: ReleaseDimensionSummary;
  maintenance: ReleaseDimensionSummary;
  changeImpact: ReleaseDimensionSummary;
  production: ReleaseDimensionSummary;
  contract: ReleaseDimensionSummary;
  flake: ReleaseDimensionSummary;
  environment: ReleaseDimensionSummary;
  environmentEvidenceBand?: 'known' | 'partial' | 'unknown' | 'conflicting' | null;
  contractDeepLink?: string | null;
  flakeDeepLink?: string | null;
  environmentDeepLink?: string | null;
  contractFlakeWeightedInVerdict?: boolean;
  environmentWeightedInVerdict?: boolean;
  legacyReleaseVerdict?: 'green' | 'amber' | 'red' | null;
  mode: 'automation_first' | 'quality_first' | 'mixed';
  evaluatedAt?: string;
}): ReleaseConfidenceResponse {
  const verdict = deriveReleaseConfidenceVerdict(input.signals);
  const action = actionFromVerdict(verdict);
  const hasBlocking = verdict === 'no_go';
  const confidence = confidenceFromEvidenceSources(input.signals.evidenceSourceCount, hasBlocking);
  const compat = gateCompatFromVerdict(verdict);
  const reasons = input.reasons.map(scrubReleaseConfidenceText).slice(0, 30);
  return {
    projectId: input.projectId,
    commitSha: input.commitSha,
    releaseId: input.releaseId ?? null,
    verdict,
    action,
    confidence,
    recommendation: recommendationForVerdict(verdict, reasons),
    reasons,
    evidence: input.evidence.slice(0, 80).map((e) => ({
      ...e,
      reason: scrubReleaseConfidenceText(e.reason),
    })),
    missingEvidence: input.missingEvidence.map(scrubReleaseConfidenceText).slice(0, 20),
    executionHealth: input.executionHealth,
    qualityGates: input.qualityGates,
    risk: input.risk,
    coverage: input.coverage,
    maintenance: input.maintenance,
    changeImpact: input.changeImpact,
    production: input.production,
    contract: input.contract,
    flake: input.flake,
    environment: input.environment,
    environmentEvidenceBand: input.environmentEvidenceBand ?? null,
    contractDeepLink: input.contractDeepLink ?? null,
    flakeDeepLink: input.flakeDeepLink ?? null,
    environmentDeepLink: input.environmentDeepLink ?? null,
    gateCompatibleExitCode: compat.exitCode,
    gateCompatibleVerdict: compat.gateVerdict,
    legacyReleaseVerdict: input.legacyReleaseVerdict ?? null,
    contractFlakeWeightedInVerdict: Boolean(input.contractFlakeWeightedInVerdict),
    environmentWeightedInVerdict: Boolean(input.environmentWeightedInVerdict),
    mode: input.mode,
    executed: false,
    rewritten: false,
    aiAdvisoryOnly: true,
    autoBlocksRelease: false,
    evaluatedAt: input.evaluatedAt ?? new Date().toISOString(),
  };
}

/**
 * Map #12 environment evidence band → Release Confidence dimension.
 * Unknown never becomes ok / positive release signal.
 */
export function mapEnvironmentBandToReleaseDimension(input: {
  band: 'known' | 'partial' | 'unknown' | 'conflicting';
  summary: string;
  sampleCount?: number;
  dimensionCount?: number;
}): {
  dimension: ReleaseDimensionSummary;
  /** True only for conflicting|partial — never for unknown|known. */
  mayWeightAsConcern: boolean;
  countsAsEvidenceSource: boolean;
} {
  const count = Math.max(input.sampleCount ?? 0, input.dimensionCount ?? 0, 1);
  const detail = `environment_intelligence · band=${input.band}`;
  if (input.band === 'unknown') {
    return {
      dimension: {
        status: 'unknown',
        headline: input.summary.slice(0, 300) || 'Insufficient environment evidence',
        detail: `${detail} · unknown ≠ healthy`,
        evidenceCount: 0,
      },
      mayWeightAsConcern: false,
      countsAsEvidenceSource: false,
    };
  }
  if (input.band === 'conflicting') {
    return {
      dimension: {
        status: 'warning',
        headline: input.summary.slice(0, 300) || 'Environment signals conflict',
        detail,
        evidenceCount: count,
      },
      mayWeightAsConcern: true,
      countsAsEvidenceSource: true,
    };
  }
  if (input.band === 'partial') {
    return {
      dimension: {
        status: 'warning',
        headline: input.summary.slice(0, 300) || 'Partial environment evidence',
        detail,
        evidenceCount: count,
      },
      mayWeightAsConcern: true,
      countsAsEvidenceSource: true,
    };
  }
  // known — context correlated; not a claim that the environment is healthy
  return {
    dimension: {
      status: 'ok',
      headline: input.summary.slice(0, 300) || 'Environment context known (not a health claim)',
      detail: `${detail} · known ≠ healthy`,
      evidenceCount: count,
    },
    mayWeightAsConcern: false,
    countsAsEvidenceSource: true,
  };
}
